import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout, LoadingScreen } from '../components/Layout';
import { api } from '../lib/api';

type Data = {
  appointments: Array<{
    id: string;
    title: string | null;
    appointmentDate: string | null;
    startTime: string | null;
    templateType: string;
  }>;
  meetings: Array<{ id: string; title: string; heldAt: string | null; topicId: string }>;
};
const pad = (n: number) => String(n).padStart(2, '0');
const localDate = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export function CalendarOverviewPage() {
  const [cursor, setCursor] = useState(() => new Date());
  const [mode, setMode] = useState<'month' | 'week'>('month');
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const weekStart = new Date(
    cursor.getFullYear(),
    cursor.getMonth(),
    cursor.getDate() - cursor.getDay(),
  );
  const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
  const start = localDate(mode === 'week' ? weekStart : monthStart);
  const end = localDate(mode === 'week' ? weekEnd : monthEnd);
  const data = useQuery({
    queryKey: ['calendar-overview', start, end],
    queryFn: () =>
      api<{ data: Data }>(`/api/calendar-overview?start=${start}&end=${end}`).then((r) => r.data),
  });
  const byDate = useMemo(() => {
    const map = new Map<string, Array<{ id: string; title: string; type: string; href: string }>>();
    for (const item of data.data?.appointments ?? [])
      if (item.appointmentDate)
        map.set(item.appointmentDate, [
          ...(map.get(item.appointmentDate) ?? []),
          {
            id: item.id,
            title: item.title ?? 'นัดหมาย',
            type: 'appointment',
            href: `/appointments/${item.id}`,
          },
        ]);
    for (const item of data.data?.meetings ?? [])
      if (item.heldAt) {
        const date = item.heldAt.slice(0, 10);
        map.set(date, [
          ...(map.get(date) ?? []),
          {
            id: item.id,
            title: item.title,
            type: 'meeting',
            href: `/meeting-topics/${item.topicId}?meeting=${item.id}`,
          },
        ]);
      }
    return map;
  }, [data.data]);
  if (data.isLoading)
    return (
      <Layout title="ปฏิทิน">
        <LoadingScreen message="กำลังโหลดปฏิทิน…" />
      </Layout>
    );
  const firstDay = monthStart.getDay();
  const days = Array.from({ length: firstDay + monthEnd.getDate() }, (_, i) =>
    i < firstDay ? null : i - firstDay + 1,
  );
  const visibleDates =
    mode === 'month'
      ? days.map((day) =>
          day ? localDate(new Date(cursor.getFullYear(), cursor.getMonth(), day)) : null,
        )
      : Array.from({ length: 7 }, (_, index) =>
          localDate(
            new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + index),
          ),
        );
  const agendaDates = [...byDate.keys()].sort();
  return (
    <Layout title="ปฏิทิน" wide>
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="workspace-eyebrow">ภาพรวมเวลา</p>
            <p className="text-sm text-[#b9d0d1]">เลือกวันหรือรายการเพื่อดูรายละเอียด</p>
          </div>
          <Link to="/create" className="btn-primary shrink-0">
            ＋ สร้าง
          </Link>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              className="btn-secondary"
              onClick={() =>
                setCursor(
                  mode === 'week'
                    ? new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - 7)
                    : new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1),
                )
              }
            >
              ‹
            </button>
            <h2 className="text-xl font-bold">
              {new Intl.DateTimeFormat('th-TH', { month: 'long', year: 'numeric' }).format(cursor)}
            </h2>
            <button
              className="btn-secondary"
              onClick={() =>
                setCursor(
                  mode === 'week'
                    ? new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 7)
                    : new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1),
                )
              }
            >
              ›
            </button>
          </div>
          <div className="flex gap-2">
            <button
              className={mode === 'month' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setMode('month')}
            >
              เดือน
            </button>
            <button
              className={mode === 'week' ? 'btn-primary' : 'btn-secondary'}
              onClick={() => setMode('week')}
            >
              สัปดาห์
            </button>
          </div>
        </div>
        <div className="card w-full md:hidden">
          <h3 className="font-semibold">
            {mode === 'week' ? 'รายการในสัปดาห์นี้' : 'รายการในเดือนนี้'}
          </h3>
          {agendaDates.length ? (
            <div className="mt-3 space-y-4">
              {agendaDates.map((date) => (
                <div key={date} className="border-l-2 border-[#8fcbb7] pl-3">
                  <p className="mb-2 text-sm font-semibold text-[#477076]">
                    {new Intl.DateTimeFormat('th-TH', { dateStyle: 'full' }).format(
                      new Date(`${date}T12:00:00`),
                    )}
                  </p>
                  <div className="space-y-2">
                    {(byDate.get(date) ?? []).map((item) => (
                      <Link
                        key={`${item.type}-${item.id}`}
                        to={item.href}
                        className="block rounded-xl border border-[#d3e1e1] bg-white p-3 font-medium text-[#173039]"
                      >
                        <span className="mr-2 text-xs text-[#5b787c]">
                          {item.type === 'meeting' ? 'ประชุม' : 'นัดหมาย'}
                        </span>
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">ยังไม่มีรายการในช่วงนี้</p>
          )}
        </div>
        <div className="card hidden w-full md:block">
          <div className="grid w-full grid-cols-7">
            <div className="p-1 text-center text-xs font-semibold">อา.</div>
            <div className="p-1 text-center text-xs font-semibold">จ.</div>
            <div className="p-1 text-center text-xs font-semibold">อ.</div>
            <div className="p-1 text-center text-xs font-semibold">พ.</div>
            <div className="p-1 text-center text-xs font-semibold">พฤ.</div>
            <div className="p-1 text-center text-xs font-semibold">ศ.</div>
            <div className="p-1 text-center text-xs font-semibold">ส.</div>
            {visibleDates.map((key, index) => {
              const day = key ? Number(key.slice(-2)) : null;
              const items = byDate.get(key ?? '') ?? [];
              return (
                <div
                  key={`${key}-${index}`}
                  className="min-h-24 overflow-hidden border border-slate-100 p-1"
                >
                  <span className="text-xs font-semibold text-slate-600">{day ?? ''}</span>
                  {items.slice(0, 3).map((item) => (
                    <Link
                      key={item.id}
                      to={item.href}
                      className={`mt-1 block truncate rounded px-1 py-0.5 text-[10px] font-medium ${item.type === 'meeting' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'}`}
                    >
                      {item.title}
                    </Link>
                  ))}
                  {items.length > 3 && (
                    <span className="text-[10px] text-slate-500">+{items.length - 3} รายการ</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <span className="text-blue-700">● นัดหมาย</span>
          <span className="text-emerald-700">● การประชุม</span>
        </div>
      </div>
    </Layout>
  );
}
